// The home screen, at /dashboard: one tab strip and the cards of the open tab (no visible title:
// the top bar and the trail already say "Dashboard").
// "Pending actions (N)" (the default) holds the decision lists, N of them with something
// waiting; "Dashboards (13)" holds the person's dashboards and the row of dashboards the
// team built. The open tab is in the address (?tab=dashboards), so Back and a reload keep
// it. Every card is the one compact card of the front pages (components/cards/CompactCard):
// an icon, a name, one number and a few words, and the whole card opens the list or
// dashboard behind it. There is no search field of the page's own: the top bar's is there.
//
// It draws at once from the menu the shell already holds: which dashboards and decision
// lists exist for this person is known before any number is asked for, so every card is
// drawn, named, in its fixed place from the first render, with a grey bar where its number
// will be. Every number then loads on its own, and nothing moves when one lands; one that is
// refused or fails is simply left out (a decision list is then still a card, with no number,
// see home/decisions). Nothing here loads a dashboard's own page or the store list. Both
// tabs' numbers are asked as the page opens, so switching tabs shows them at once.
//
// The Hiring card's number is the one read live: the Notice period tab's count, read from
// 1Office in this person's own sign-in (no background reader has one), with a short wait of
// its own (HOME_NOTICE_WAIT_MS) so a slow 1Office cannot hold the screen.
//
// Some of the numbers (what waits on My Approvals, requests still waiting, people who had
// not checked in, stores short of people) are kept by the server in the background. Each
// has the time its facts were read in its card's hover. On the first open after a quiet
// spell the server may not know one yet: it is then left out like any other missing
// number, and asked for again, so it appears without a reload (see home/useRead).
//
// Holding a page's menu row is not a promise of access. Some pages check a second
// permission and show their own "access needed" panel; that is left to the page.
import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Inbox, LayoutGrid } from 'lucide-react';
import MasterTabStrip, { type MasterTab } from '../components/MasterTabStrip';
import { COMPACT_CARD_GRID, CompactCardPlaceholder } from '../components/cards/CompactCard';
import { Skeleton } from '../components/Skeleton';
import { HOME_DASHBOARDS_TAB, HOME_TAB_PARAM } from '../navigation/dashboards';
import { pageMenuItems } from '../navigation/menuTree';
import { hiringUserKeyOf, useHiringAccess } from '../components/hiring/useHiringAccess';
import { useAuthStore } from '../stores/authStore';
import { useMenuStore } from '../stores/menuStore';
import DashboardCards from './home/DashboardCards';
import DecisionList from './home/DecisionList';
import TeamDashboards from './home/TeamDashboards';
import { buildDecisionList, hasDecisionCards, heldDecisionLists, waitingListCount } from './home/decisions';
import { holdsAnyPage, homeDashTabs } from './home/homeMenu';
import { todayInIndia } from './home/homeNumbers';
import { useDashboardNumbers, useDecisionCounts, useTeamDashboards } from './home/useHomeNumbers';

// The page's own measure: what the section pages use, so the two kinds of screen line up.
const PAGE = 'mx-auto w-full max-w-[1352px] pb-2 pt-4 sm:px-2';

/** The two tabs. Pending actions is the default and leaves the address plain. */
type HomeTab = 'attention' | 'dashboards';

/** Until the menu has arrived: the strip's place, and a row of cards' places. */
function Loading(): ReactElement {
  return (
    <div role="status" aria-label="Loading" className="opacity-60">
      <Skeleton className="mt-3.5 h-[38px] w-72 max-w-full rounded-lg" />
      <div className={`mt-4 ${COMPACT_CARD_GRID}`}>
        <CompactCardPlaceholder />
        <div className="hidden sm:block"><CompactCardPlaceholder /></div>
        <div className="hidden lg:block"><CompactCardPlaceholder /></div>
        <div className="hidden xl:block"><CompactCardPlaceholder /></div>
      </div>
    </div>
  );
}

export default function HomePage(): ReactElement {
  const items = useMenuStore((s) => s.items);
  const loaded = useMenuStore((s) => s.loaded);
  const user = useAuthStore((s) => s.user);
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  // No project yet is null, whatever an older saved sign-in left in its place.
  const projectId = selectedProjectId || null;
  const isSuperAdmin = user?.adminRole === 'super_admin';

  const pages = useMemo(
    () => pageMenuItems(items, { isSuperAdmin, selectedProjectId: projectId }),
    [items, isSuperAdmin, projectId],
  );
  // Not before the menu has arrived: an empty menu that has LOADED keeps every dashboard
  // (see homeDashTabs), and until then it is empty for everyone.
  const heldTabs = useMemo(() => (loaded ? homeDashTabs(items) : []), [items, loaded]);
  // Hiring's card only where the server says this person may see hiring in this project
  // (its tick, and the project's switch). Asked only of someone who holds its menu row.
  const hiring = useHiringAccess(heldTabs.includes('Hiring') ? projectId : null, hiringUserKeyOf(user));
  const tabs = useMemo(
    () => (hiring.canView ? heldTabs : heldTabs.filter((t) => t !== 'Hiring')),
    [heldTabs, hiring.canView],
  );

  // The Hiring card also needs what the server's hiring access lets this person do there.
  // While the server has not answered someone who holds its row, the card holds its place.
  const { canManage: hiringCanManage, canSeeRequests: hiringCanSeeRequests } = hiring;
  const hiringAsking = heldTabs.includes('Hiring') && projectId !== null && hiring.state === 'unknown';
  // My Approvals shows "access needed" to a sign-in with no 1Office user id: no card for it then.
  const opensMyApprovals = Boolean(user?.userId);
  const held = useMemo(
    () => heldDecisionLists(
      pages,
      tabs,
      { canManage: hiringCanManage, canSeeRequests: hiringCanSeeRequests, asking: hiringAsking },
      { opensMyApprovals },
    ),
    [pages, tabs, hiringCanManage, hiringCanSeeRequests, hiringAsking, opensMyApprovals],
  );
  const {
    redAppeals, yellowAppeals, approvals, exitReviews, plansToDecide, probation, noBuddy, storeRequests, posts, noticeHiring,
  } = useDecisionCounts(projectId, {
    redAppeals: Boolean(held.redAppeals),
    yellowAppeals: Boolean(held.yellowAppeals),
    approvals: Boolean(held.approvals),
    exitReviews: Boolean(held.startExit),
    plansToDecide: Boolean(held.pip),
    probation: Boolean(held.probation),
    noBuddy: held.onboarding,
    storeRequests: Boolean(held.stores),
    posts: Boolean(held.socialModeration),
    // Counted only once the server has said this person may act there.
    noticeHiring: held.noticeHiring === true,
  });
  // The Onboarding card shows the count the "waiting for a buddy" card was just asked for.
  const numbers = useDashboardNumbers(projectId, tabs, noBuddy);
  // Asked only of someone who holds the Dashboard: the row sits under its cards.
  const team = useTeamDashboards(tabs.length > 0 ? projectId : null);

  const [today] = useState(() => todayInIndia());
  const decisions = useMemo(
    () => buildDecisionList(
      held,
      { redAppeals, yellowAppeals, approvals, exitReviews, plansToDecide, probation, noBuddy, storeRequests, posts, noticeHiring },
      today,
    ),
    [held, redAppeals, yellowAppeals, approvals, exitReviews, plansToDecide, probation, noBuddy, storeRequests, posts, noticeHiring, today],
  );

  // The open tab, from the address. A tab with nothing in it is not offered; when the one
  // asked for is such a tab, the other is shown (the address is left as it is).
  const [searchParams, setSearchParams] = useSearchParams();
  const asked: HomeTab = searchParams.get(HOME_TAB_PARAM) === HOME_DASHBOARDS_TAB ? 'dashboards' : 'attention';
  const hasAttention = hasDecisionCards(decisions);
  const hasDashboards = tabs.length > 0;
  // Once a plain address has shown "Dashboards" (there was nothing to attend to), a list that
  // turns up later does not switch the open tab under the person: it is offered in the strip,
  // and opened when they pick it. Kept per project.
  const fellBack = asked === 'attention' && !hasAttention && hasDashboards;
  const [dashboardsShownFor, setDashboardsShownFor] = useState<number | null | undefined>(undefined);
  if (fellBack && dashboardsShownFor !== projectId) setDashboardsShownFor(projectId);
  const keepDashboards = asked === 'attention' && hasDashboards && (fellBack || dashboardsShownFor === projectId);
  const open: HomeTab = asked === 'dashboards' ? (hasDashboards ? 'dashboards' : 'attention') : (keepDashboards ? 'dashboards' : 'attention');
  const strip: MasterTab<HomeTab>[] = [
    ...(hasAttention ? [{ key: 'attention' as const, label: 'Pending actions', icon: Inbox, count: waitingListCount(decisions) }] : []),
    ...(hasDashboards ? [{ key: 'dashboards' as const, label: 'Dashboards', icon: LayoutGrid, count: tabs.length }] : []),
  ];
  // A new entry in the history, so Back returns to the tab before.
  const selectTab = (tab: HomeTab) => {
    if (tab === open) return;
    // Picking a tab ends the hold above: from here the address says which tab is open.
    if (dashboardsShownFor !== undefined) setDashboardsShownFor(undefined);
    // "Pending actions" on a plain address that showed "Dashboards": the address already says it.
    if (tab === 'attention' && asked === 'attention') return;
    const next = new URLSearchParams(searchParams);
    if (tab === 'dashboards') next.set(HOME_TAB_PARAM, HOME_DASHBOARDS_TAB);
    else next.delete(HOME_TAB_PARAM);
    setSearchParams(next);
  };

  return (
    <div className={PAGE}>
      {/* The top bar and the trail already say "Dashboard": no visible title of its own, so the
          name is not said three times. Kept for screen readers. */}
      <h1 className="sr-only">Dashboard</h1>

      {!loaded ? (
        <Loading />
      ) : (
        <>
          {strip.length > 0 && (
            // The master pages' strip. On a phone its icons are left out so both tabs fit on one line.
            <div className="max-sm:[&_svg]:hidden">
              <MasterTabStrip tabs={strip} active={open} onSelect={selectTab} />
            </div>
          )}
          {/* Both tabs are drawn, the closed one hidden, so switching shows its cards at once. */}
          {hasAttention && (
            <div hidden={open !== 'attention'} className="mt-4">
              <DecisionList list={decisions} />
            </div>
          )}
          {hasDashboards && (
            <div hidden={open !== 'dashboards'} className="mt-4">
              <DashboardCards tabs={tabs} numbers={numbers}>
                {team.status === 'ready' && projectId !== null && <TeamDashboards projectId={projectId} team={team.value} />}
              </DashboardCards>
            </div>
          )}
          {/* Rows, but none of them a page or the Dashboard: headings with nothing under them. */}
          {tabs.length === 0 && !holdsAnyPage(pages) && (
            <div className="mt-5 rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-700 shadow-sm">
              You do not have any pages yet. Ask your project admin if you need one.
            </div>
          )}
        </>
      )}
    </div>
  );
}
